import { Fragment, useState } from "react";
import { UserIcon } from "lucide-react";

import { AwaitingStevieSectionRow } from "./AwaitingStevieSectionRow";
import { CandidateCell } from "./CandidateCell";
import { CollapsibleStageRow } from "./CollapsibleStageRow";
import { NextStepCell } from "./NextStepCell";
import { AddCandidateDialog } from "../AddCandidateDialog";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { CandidacyStageChip } from "@jobs/pipeline/shared/CandidacyStageChip";
import { LastPingDisplay } from "@jobs/pipeline/shared/LastPingDisplay";
import { LocationAndCommute } from "@jobs/pipeline/shared/LocationAndCommute";
import { TimeInStageDisplay } from "@jobs/pipeline/shared/TimeInStageDisplay";
import {
  CandidacyOwnerCell,
  Column,
  EmptyDataDash,
  EmptyState,
  SortableHeader,
} from "@/components";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { agenticScoreToBucket } from "@/lib/agenticScore";
import {
  hiddenPipelineCandidacyStatuses,
  jobPipelineAscLeadingSortKeys,
  pipelineStageToDefaultCandidacyStatus,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cycleColumnSort } from "@/lib/sort";
import { stevieRoute } from "@/lib/stevieRoute";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  JobPipelineSortKey,
  JobPipelineSortState,
  MinimalCandidacy,
  PipelineStage,
  PipelineViewPreference,
} from "@/types";

// Candidate / Staffer / Agentic / Time in stage / Last ping / Location / Stage / Next step
const COLUMN_WIDTHS = ["21%", "6%", "10%", "13%", "10%", "19%", "8%", "13%"];

const isHiddenStatusCandidacy = (candidacy: MinimalCandidacy): boolean =>
  hiddenPipelineCandidacyStatuses.includes(
    candidacy.lastCandidacyStatusChange.toStatus,
  );

const isAscLeadingPipelineSortKey = (key: JobPipelineSortKey): boolean =>
  jobPipelineAscLeadingSortKeys.includes(key);

export const JobPipelineTable = ({
  hasAnyCandidacies,
  isFilteredEmpty,
  isShortlistBlocked,
  onCandidacyClick,
  onOpenMessages,
  onReject,
  onRequestStatusChangeDialog,
  onReviewAllApplied,
  onShortlist,
  setAppliedRegionRef,
  setSortState,
  sortState,
  stageGroups,
}: {
  hasAnyCandidacies: boolean;
  isFilteredEmpty: boolean;
  isShortlistBlocked: boolean;
  onCandidacyClick: (candidacy: MinimalCandidacy) => void;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  onReject: (candidacy: MinimalCandidacy) => void;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
  onReviewAllApplied: (appliedCandidacyIds: string[]) => void;
  onShortlist: (candidacy: MinimalCandidacy) => void;
  setAppliedRegionRef: (el: HTMLElement | null) => void;
  setSortState: (next: JobPipelineSortState | null) => void;
  sortState: JobPipelineSortState | null;
  stageGroups: { stage: PipelineStage; rows: MinimalCandidacy[] }[];
}) => {
  /* React Hooks */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();

  const [collapsedStages, setCollapsedStages] = useState<Set<PipelineStage>>(
    new Set(),
  );
  const [addCandidateStage, setAddCandidateStage] =
    useState<PipelineStage | null>(null);

  /* Handlers */
  const toggleStageCollapsed = (stage: PipelineStage) => {
    setCollapsedStages((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };

  const handleSort = (key: JobPipelineSortKey) => {
    const next = cycleColumnSort(sortState, key, isAscLeadingPipelineSortKey);
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Sort by",
      next ? `${next.key} ${next.direction}` : "none",
      { tab_name: "Pipeline", pipeline_view: PipelineViewPreference.TABLE },
    );
    setSortState(next);
  };

  const handleRowClick = (candidacy: MinimalCandidacy) => {
    onCandidacyClick(candidacy);
  };

  const handleAddCandidate = (stage: PipelineStage) => {
    mixpanelUtils.trackButtonClick("Add candidate", MIXPANEL_SCREENS.JOB, {
      pipeline_view: PipelineViewPreference.TABLE,
      stage,
    });
    setAddCandidateStage(stage);
  };

  const handleToggleStageCollapsed = (stage: PipelineStage) => {
    const wasCollapsed = collapsedStages.has(stage);
    mixpanelUtils.trackButtonClick(
      "Toggle pipeline stage",
      MIXPANEL_SCREENS.JOB,
      {
        pipeline_view: PipelineViewPreference.TABLE,
        stage,
        collapsed: !wasCollapsed,
      },
    );
    toggleStageCollapsed(stage);
  };

  /* Render */
  const renderCandidacyRow = (
    candidacy: MinimalCandidacy,
    stage: PipelineStage,
    // Sent to Stevie and waiting on the call — muted, like the kanban card.
    isAwaitingStevie = false,
  ) => {
    const agenticBucket = agenticScoreToBucket(
      candidacy.agenticScores[0]?.toScore,
    );
    const isHidden = isHiddenStatusCandidacy(candidacy);
    return (
      <TableRow
        className={cn(
          "cursor-pointer border-b border-slate-100",
          isHidden
            ? "bg-red-50 hover:bg-red-100"
            : "odd:bg-slate-50/60 hover:bg-slate-100",
          isAwaitingStevie && "opacity-70",
        )}
        key={candidacy.id}
        onClick={() => {
          handleRowClick(candidacy);
        }}
      >
        <CandidateCell
          candidacy={candidacy}
          showCommunicationModeIcon={stage === PipelineStage.IN_COMMUNICATION}
        />
        <TableCell className="py-3">
          <CandidacyOwnerCell
            candidacyId={candidacy.id}
            candidacyStatus={candidacy.lastCandidacyStatusChange.toStatus}
            onAgentStarted={() => {
              setCandidacyInAllLists({
                candidacyId: candidacy.id,
                stafferAgentActive: true,
              });
            }}
            owners={candidacy.owners}
            promptAgentOnClaim
          />
        </TableCell>
        <TableCell className="py-3">
          {agenticBucket ? (
            <AgenticScoreChip
              bucket={agenticBucket}
              isHumanRated={candidacy.agenticScores[0]?.userId != null}
              justification={candidacy.agenticScores[0]?.justification}
            />
          ) : (
            <EmptyDataDash />
          )}
        </TableCell>
        <TableCell className="py-3">
          <TimeInStageDisplay candidacy={candidacy} />
        </TableCell>
        <TableCell className="py-3">
          {candidacy.lastPing ? (
            <LastPingDisplay candidacy={candidacy} showTooltip stage={stage} />
          ) : (
            <EmptyDataDash />
          )}
        </TableCell>
        <TableCell className="py-3">
          <LocationAndCommute
            commuteInfo={candidacy.commuteInfo}
            location={candidacy.location}
            variant="sm"
          />
        </TableCell>
        <TableCell
          className="py-3"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CandidacyStageChip
            candidacy={candidacy}
            stage={stage}
            onRequestStatusChangeDialog={onRequestStatusChangeDialog}
          />
        </TableCell>
        <NextStepCell
          candidacy={candidacy}
          isShortlistBlocked={isShortlistBlocked}
          stage={stage}
          onOpenMessages={onOpenMessages}
          onReject={onReject}
          onShortlist={onShortlist}
        />
      </TableRow>
    );
  };

  const renderRowsBasedOnStage = (
    stage: PipelineStage,
    rows: MinimalCandidacy[],
  ) => {
    // Candidacies waiting on a Stevie call drop below the ones still needing a
    // decision — they're already handled until the call lands.
    if (stage === PipelineStage.APPLIED) {
      const awaitingStevie = rows.filter(stevieRoute.isAwaitingCall);
      if (awaitingStevie.length > 0) {
        const toReview = rows.filter(
          (candidacy) => !stevieRoute.isAwaitingCall(candidacy),
        );
        return (
          <>
            {toReview.map((candidacy) => renderCandidacyRow(candidacy, stage))}
            <AwaitingStevieSectionRow
              colSpan={COLUMN_WIDTHS.length}
              count={awaitingStevie.length}
            />
            {awaitingStevie.map((candidacy) =>
              renderCandidacyRow(candidacy, stage, true),
            )}
          </>
        );
      }
    }

    return rows.map((candidacy) => renderCandidacyRow(candidacy, stage));
  };

  const renderContent = () => {
    if (!hasAnyCandidacies) {
      return (
        <EmptyState
          className="bg-slate-50 py-14"
          description="Start searching to find and shortlist qualified candidates for this job."
          handlePrimaryClick={() => {
            handleAddCandidate(PipelineStage.APPLIED);
          }}
          headerClassName="text-xl"
          Icon={UserIcon}
          primaryButtonName="Add candidate"
          title="No candidates yet"
        />
      );
    }

    if (isFilteredEmpty) {
      return (
        <EmptyState
          className="bg-slate-50 py-14"
          description="No candidates match the current filters."
          headerClassName="text-xl"
          Icon={UserIcon}
          title="No candidates found"
        />
      );
    }

    return (
      <Column
        ref={setAppliedRegionRef}
        className="relative h-fit max-h-full min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white"
      >
        <div className="max-h-full overflow-auto overscroll-contain">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {COLUMN_WIDTHS.map((width, i) => (
                <col key={i} style={{ width }} />
              ))}
            </colgroup>
            <TableHeader className="sticky top-0 z-30 bg-white [&_th]:bg-white [&_th]:py-3 [&_th]:font-semibold [&_th]:text-slate-700 [&_th]:shadow-[0_1px_0_0_rgb(226_232_240)] dark:[&_th]:bg-[#4C4F52] dark:[&_th]:text-[#FAFAFA] dark:[&_th]:shadow-[0_1px_0_0_#77787A] [&_tr]:border-b-0">
              <TableRow>
                <SortableHeader
                  sortKey={JobPipelineSortKey.Name}
                  label="Candidate"
                  sort={sortState}
                  onSort={handleSort}
                />
                <SortableHeader
                  sortKey={JobPipelineSortKey.Staffer}
                  label="Staffer"
                  sort={sortState}
                  onSort={handleSort}
                />
                <SortableHeader
                  sortKey={JobPipelineSortKey.AgenticScore}
                  label="Agentic score"
                  sort={sortState}
                  onSort={handleSort}
                />
                <SortableHeader
                  sortKey={JobPipelineSortKey.TimeInStage}
                  label="Time in stage"
                  sort={sortState}
                  onSort={handleSort}
                />
                <SortableHeader
                  sortKey={JobPipelineSortKey.LastPing}
                  label="Last ping"
                  sort={sortState}
                  onSort={handleSort}
                />
                <SortableHeader
                  sortKey={JobPipelineSortKey.Location}
                  label="Location"
                  sort={sortState}
                  onSort={handleSort}
                />
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Next step</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stageGroups
                .filter((group) => group.rows.length > 0)
                .map(({ stage, rows }) => {
                  const isCollapsed = collapsedStages.has(stage);
                  return (
                    <Fragment key={stage}>
                      <CollapsibleStageRow
                        stage={stage}
                        rowCount={rows.length}
                        isCollapsed={isCollapsed}
                        colSpan={COLUMN_WIDTHS.length}
                        onToggleCollapsed={() => {
                          handleToggleStageCollapsed(stage);
                        }}
                        onAddCandidate={() => {
                          handleAddCandidate(stage);
                        }}
                        onReviewAllApplied={() => {
                          // Candidacies waiting on a Stevie call need no
                          // decision yet.
                          onReviewAllApplied(
                            rows
                              .filter((row) => !stevieRoute.isAwaitingCall(row))
                              .map((row) => row.id),
                          );
                        }}
                      />
                      {!isCollapsed && renderRowsBasedOnStage(stage, rows)}
                    </Fragment>
                  );
                })}
            </TableBody>
          </table>
        </div>
      </Column>
    );
  };

  const render = () => (
    <>
      {renderContent()}
      <AddCandidateDialog
        defaultStatus={
          addCandidateStage
            ? pipelineStageToDefaultCandidacyStatus[addCandidateStage]
            : undefined
        }
        onOpenChange={(next) => {
          if (!next) setAddCandidateStage(null);
        }}
        open={addCandidateStage !== null}
      />
    </>
  );

  return render();
};
